import { useCallback, useMemo, useState, useEffect } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Cell,
  LabelList,
} from "recharts";
import { AlertCircle, CalendarClock, Clock, Info, LogIn, LogOut, MapPin, X } from "lucide-react";
import { TruncatedCellTooltip } from "../../../../ui/responsive-table/truncated-cell-tooltip.tsx";

import { useTodayPunch } from "../../../../../hooks/use-today-punch.ts";
import { Card } from "../../../../shared/card/card.tsx";
import { AttendanceEmptyState } from "../attendance-empty-state.tsx";
import { WeeklyTooltip } from "./components/weekly-tooltip.tsx";
import { Link } from "react-router-dom";
import { formatLocalDate } from "../../../../../utils/helpers.ts";

import {
  CHART_Y_MAX,
  LEGEND_ITEMS,
  STATUS_COLORS,
  STATUS_LABELS,
  STATUS_OPACITY,
} from "./constants.ts";
import {
  buildChartData,
  comparePunchToCurrentShift,
  formatActiveWorkingHoursDisplay,
  formatFullDate,
  formatLateDuration,
  formatTimeToTwelveHour,
  isRegularTimingEffective,
} from "./utils.ts";
import type { ChartEntry } from "./types.ts";
import styles from "./attendance-card.module.css";
const SafeResponsiveContainer = ResponsiveContainer as any;

type AttendanceCardProps = {
  isSystemAssigned: boolean;
};

export const AttendanceCard = ({ isSystemAssigned }: AttendanceCardProps) => {
  const { weekData, loading, error, fetch } = useTodayPunch();
  const showEmptyState = !loading && (Boolean(error) || weekData.length === 0);
  const [selectedEntry, setSelectedEntry] = useState<ChartEntry | null>(null);

  useEffect(() => {
    fetch();
  }, [fetch]);

  /** Local calendar date — must match API `date` (`YYYY-MM-DD`), not UTC from `toISOString()`. */
  const todayISO = formatLocalDate(new Date());

  const todayData = useMemo(() => {
    return weekData?.find((d) => d.date === todayISO);
  }, [weekData, todayISO]);

  const chartData = useMemo(() => {
    return buildChartData(weekData ?? [], todayData);
  }, [weekData, todayData]);

  /** Same week payload as the chart — no second `?date=` request on bar click. */
  const activeData = useMemo(() => {
    if (!selectedEntry) {
      return todayData ?? null;
    }
    return weekData?.find((d) => d.date === selectedEntry.isoDate) ?? null;
  }, [selectedEntry, weekData, todayData]);

  const isHistoricalDay = Boolean(selectedEntry && selectedEntry.isoDate !== todayISO);

  const statsPending = activeData == null && loading;

  const handleBarClick = useCallback(
    (data: any) => {
      const barData = data?.payload;

      if (!barData?.isoDate) return;

      if (barData.isoDate === todayISO || selectedEntry?.isoDate === barData.isoDate) {
        setSelectedEntry(null);
        return;
      }

      setSelectedEntry(barData);
    },
    [selectedEntry, todayISO],
  );

  const displayIn = formatTimeToTwelveHour(activeData?.inTime);
  const displayOut = formatTimeToTwelveHour(activeData?.outTime);
  const displayHours = activeData?.workingHours ?? "0h 0m";
  const displayActiveWorking = formatActiveWorkingHoursDisplay(
    activeData?.activeWorkingHours,
  );
  const geoLocationLabel =
    activeData != null && activeData.location != null
      ? String(activeData.location).trim()
      : "";
  const hasGeoLocation = geoLocationLabel !== "";
  // Prefer timing recorded for the selected day. Older API rows have no timing;
  // compare their punch with today's configured shift only as a labelled preview.
  const timingData = isRegularTimingEffective(activeData?.date) ? activeData : null;
  const currentShiftComparison = timingData && isHistoricalDay && !timingData.officeStartTime
    && timingData?.arrivalStatus == null
    ? comparePunchToCurrentShift(
        timingData?.inTime,
        todayData?.officeStartTime,
        todayData?.graceEndsAt,
      )
    : null;
  const usingCurrentShift = currentShiftComparison != null;
  const officeStartTime = timingData?.officeStartTime
    ?? (usingCurrentShift ? todayData?.officeStartTime : null);
  const graceEndsAt = timingData?.graceEndsAt
    ?? (usingCurrentShift ? todayData?.graceEndsAt : null);
  const hasRegularTiming = Boolean(officeStartTime);
  const isAfterGrace = timingData?.arrivalStatus != null
    ? timingData.arrivalStatus === "AFTER_GRACE"
    : currentShiftComparison?.isAfterGrace === true;
  const lateArrivalMessage = isAfterGrace
    ? currentShiftComparison != null
      ? `You were late by ${formatLateDuration(currentShiftComparison.minutesAfterStart)} on ${formatFullDate(selectedEntry?.isoDate)} (current shift).`
      : timingData?.minutesAfterStart != null
      ? `You were late by ${formatLateDuration(timingData.minutesAfterStart)} ${isHistoricalDay ? "that day" : "today"}.`
      : `You arrived after your grace period ${isHistoricalDay ? "that day" : "today"}.`
    : null;

  const renderLateMarker = ({ viewBox, index }: any) => {
    const entry = chartData[index];
    if (!entry?.isLate || entry.hours <= 0 || !viewBox) return null;
    const centerX = Number(viewBox.x) + Number(viewBox.width) / 2;
    const topY = Number(viewBox.y) - 30;
    if (!Number.isFinite(centerX) || !Number.isFinite(topY)) return null;
    return (
      <g aria-hidden="true" pointerEvents="none" opacity={selectedEntry && selectedEntry.isoDate !== entry.isoDate ? 0.35 : 1}>
        <rect x={centerX - 21} y={topY} width={42} height={22} rx={9} fill="var(--color-amber-100)" />
        <path d={`M ${centerX - 5} ${topY + 21} L ${centerX} ${topY + 27} L ${centerX + 5} ${topY + 21} Z`} fill="var(--color-amber-100)" />
        <text x={centerX} y={topY + 15} textAnchor="middle" fill="var(--color-amber-800)" fontSize={11} fontWeight={600}>Late</text>
      </g>
    );
  };

  return (
    <Card
      variant="default"
      padding="3xl"
      className={showEmptyState ? `${styles.attendanceCard} ${styles.emptyCard}` : styles.attendanceCard}
    >
      {/* Header */}
      <div className={styles.cardHeader}>
        <div className={styles.cardTitle}>
          <h5 className="heading-sm">{isHistoricalDay ? "Attendance" : "Today's Attendance"}</h5>
          {/* <span className="badge--success">Working</span> */}
        </div>
        <div className={styles.headerActions}>
          <Link
            to="/dashboard/myConcern"
            className="badge--danger gap-2 d-flex align-items-center"
          >
            <AlertCircle size={12} />
            Raise Concern
          </Link>
        </div>
      </div>

      {showEmptyState ? (
        <AttendanceEmptyState
          icon={CalendarClock}
          title="Attendance Details Unavailable"
          description="Unable to display attendance details at the moment."
        />
      ) : (
      <>

      <div className={styles.mainContent}>

        {isHistoricalDay && selectedEntry && (
          <div className={styles.selectedDayLabel}>
            <span>Showing: {formatFullDate(selectedEntry.isoDate)}</span>
            <button
              type="button"
              className={styles.selectedDayReset}
              onClick={() => {
                setSelectedEntry(null);
              }}
            >
              <X size={14} aria-hidden="true" />
              Back to today
            </button>
          </div>
        )}

        {hasRegularTiming && (
          <div className={styles.shiftStart}>
            <div className={styles.shiftStartLabel}>
              <Clock size={17} aria-hidden="true" />
              <span>{usingCurrentShift ? "Current office start time" : "Your office start time"}</span>
            </div>
            <strong className={styles.shiftStartValue}>
              {formatTimeToTwelveHour(officeStartTime)}
            </strong>
            <span className={styles.graceNote}>
              Grace until {formatTimeToTwelveHour(graceEndsAt)}
            </span>
          </div>
        )}

        <div className="row g-2">
          <div className="col-6">
            <div className={`info-card ${styles.punchTile}`}>
              <div className="d-flex align-items-center gap-1 small text-muted">
                <LogIn size={14} className={styles.officeInTimeIcon} />
                <p className="sub-heading-xs"> Actual Punch In</p>
              </div>

              <div className={styles.officeIn}>
                {statsPending ? "—" : displayIn}
              </div>
            </div>
          </div>

          <div className="col-6">
            <div className={`neutral-card ${styles.punchTile}`}>
              <div className="d-flex align-items-center gap-1 small text-muted">
                <LogOut size={14} />
                <p className="sub-heading-xs"> Actual Punch Out </p>
              </div>

              <div className={styles.officeOut}>
                {statsPending ? "—" : displayOut}
              </div>
            </div>
          </div>
        </div>

        {lateArrivalMessage && (
          <div className={styles.lateArrivalBanner} role="status">
            <span className={styles.lateArrivalIcon}><Info size={17} aria-hidden="true" /></span>
            <span>{lateArrivalMessage}</span>
          </div>
        )}

        {/* Stats */}
        <div className="mt-3 gap-2 d-flex flex-column">


          <div className="d-flex justify-content-between">
            <span className="sub-heading-md">Office Hours</span>
            <span className={`fw-semibold ${styles.displayHours}`}>
              {statsPending ? "—" : displayHours}
            </span>
          </div>

          {isSystemAssigned ? (
            <div className="d-flex justify-content-between">
              <span className="sub-heading-md">Active Work Hours</span>
              <span className={`fw-semibold ${styles.activeWorkHours}`}>
                {statsPending ? "—" : displayActiveWorking}
              </span>
            </div>
          ) : null}

          {hasGeoLocation ? (
            <div className="d-flex justify-content-between align-items-center gap-2">
              <span className="sub-heading-md d-flex align-items-center gap-1 flex-shrink-0">
                <MapPin size={13} className={styles.locationIcon} aria-hidden />
                Geo Location
              </span>
              <TruncatedCellTooltip
                tooltipText={geoLocationLabel}
                truncatedClassName={styles.locationPill}
              >
                {geoLocationLabel}
              </TruncatedCellTooltip>
            </div>
          ) : null}
        </div>
      </div>
      {/* Weekly Bar Chart */}
      <div className={styles.chartWrapper}>
        {loading ? (
          <div className={styles.chartLoading}>Loading chart…</div>
        ) : (
          <div className={styles.chartContainer}>
            <SafeResponsiveContainer width="100%" height="100%">
              <BarChart
                data={chartData}
                barCategoryGap="30%"
                margin={{ top: 30, right: 0, left: 0, bottom: 0 }}
                onClick={handleBarClick}
                style={{ cursor: "pointer" }}
              >
                <XAxis
                  dataKey="shortDay"
                  axisLine={false}
                  tickLine={false}
                  interval={0}
                  minTickGap={0}
                  tick={{ fontSize: 11, fill: "var(--content-muted)" }}
                />
                <YAxis hide domain={[0, CHART_Y_MAX]} />
                <Tooltip cursor={false} content={<WeeklyTooltip />} />
                <Bar
                  dataKey="hours"
                  radius={[4, 4, 0, 0]}
                  onClick={handleBarClick}
                  maxBarSize={80}
                >
                  {chartData.map((entry, i) => (
                    <Cell
                      key={`cell-${i}`}
                      fill={entry.isLate ? "var(--color-amber-500)" : STATUS_COLORS[entry.status]}
                      opacity={
                        selectedEntry && selectedEntry.isoDate !== entry.isoDate
                          ? 0.35 // dim unselected bars
                          : STATUS_OPACITY[entry.status]
                      }
                    />
                  ))}
                  <LabelList dataKey="hours" content={renderLateMarker} />
                </Bar>
              </BarChart>
            </SafeResponsiveContainer>
          </div>
        )}

        {/* Legend */}
        <div className={styles.legend}>
          {LEGEND_ITEMS.map((s) => (
            <div key={s} className={styles.legendItem}>
              <div
                className={styles.legendDot}
                style={{
                  background: STATUS_COLORS[s],
                  opacity: STATUS_OPACITY[s],
                }}
              />
              <span className={styles.legendLabel}>{STATUS_LABELS[s]}</span>
            </div>
          ))}
        </div>
      </div>
      </>
      )}
    </Card>
  );
};
